import { queryOptions, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, LogOut, Pencil, Plus, Trash2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { slugify } from "@/lib/blog-meta";
import {
  createPost,
  deletePost,
  listAllPosts,
  updatePost,
  type BlogPost,
} from "@/lib/blog.functions";

const adminPostsQuery = queryOptions({
  queryKey: ["blog", "admin"],
  queryFn: () => listAllPosts(),
});

export const Route = createFileRoute("/_authenticated/admin/blog")({
  loader: ({ context }) => context.queryClient.ensureQueryData(adminPostsQuery),
  head: () => ({
    meta: [{ title: "Gerenciar blog — H2J" }, { name: "robots", content: "noindex" }],
  }),
  component: AdminBlogPage,
  errorComponent: AdminError,
});

function AdminError() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-2xl font-semibold text-foreground">Acesso restrito</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Sua conta não tem permissão para gerenciar o blog.
        </p>
      </div>
    </div>
  );
}

interface FormState {
  id: string | null;
  title: string;
  content: string;
  slug: string;
}

const emptyForm: FormState = { id: null, title: "", content: "", slug: "" };

function AdminBlogPage() {
  const { data: posts } = useSuspenseQuery(adminPostsQuery);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["blog"] });

  const startEdit = (post: BlogPost) => {
    setForm({ id: post.id, title: post.title, content: post.content, slug: post.slug });
    setError("");
  };

  const save = async (status: "draft" | "published") => {
    if (!form) return;
    if (!form.title.trim()) {
      setError("Preencha o título.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = { title: form.title, content: form.content, status };
      if (form.id) {
        await updatePost({ data: { id: form.id, ...payload } });
      } else {
        await createPost({ data: payload });
      }
      setForm(null);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (post: BlogPost) => {
    if (!window.confirm(`Excluir o post "${post.title}"? Essa ação não pode ser desfeita.`)) return;
    await deletePost({ data: post.id });
    await refresh();
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  };

  const address = form?.id ? form.slug : slugify(form?.title ?? "");

  return (
    <main className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border/70">
        <div className="section-shell flex h-18 items-center justify-between">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold">
            <ArrowLeft className="size-4" /> Ver blog
          </Link>
          <Button variant="ghost" size="sm" onClick={signOut}>
            <LogOut className="size-4" /> Sair
          </Button>
        </div>
      </header>

      <section className="section-shell max-w-4xl py-12">
        <div className="flex items-center justify-between gap-4">
          <div>
            <p className="eyebrow">Administração</p>
            <h1 className="mt-3 text-3xl font-semibold">Posts do blog</h1>
          </div>
          {!form && (
            <Button variant="premium" onClick={() => setForm({ ...emptyForm })}>
              <Plus className="size-4" /> Novo post
            </Button>
          )}
        </div>

        {form ? (
          <div className="mt-10 grid gap-6 border border-border p-6 sm:p-8">
            <div className="grid gap-2">
              <Label htmlFor="title">Assunto</Label>
              <Input
                id="title"
                placeholder="Sobre o que é este artigo?"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                {form.id
                  ? `/blog/${address} (endereço fixo)`
                  : `/blog/${address || "…"}`}
              </p>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="content">Texto</Label>
              <Textarea
                id="content"
                rows={16}
                placeholder="Escreva o artigo aqui."
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
              />
              <p className="text-xs text-muted-foreground">
                Separe parágrafos com uma linha em branco. Use ## para subtítulos, - para listas e
                **texto** para negrito. O resumo, o endereço e a data são gerados sozinhos.
              </p>
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <div className="flex flex-wrap gap-3">
              <Button variant="premium" onClick={() => save("published")} disabled={saving}>
                {saving ? "Salvando…" : form.id ? "Publicar alterações" : "Publicar"}
              </Button>
              <Button variant="quiet" onClick={() => save("draft")} disabled={saving}>
                Salvar como rascunho
              </Button>
              <Button variant="ghost" onClick={() => setForm(null)} disabled={saving}>
                Cancelar
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-10 divide-y divide-border border border-border">
            {posts.length === 0 && (
              <p className="p-8 text-center text-sm text-muted-foreground">
                Nenhum post ainda. Clique em "Novo post" para começar.
              </p>
            )}
            {posts.map((post) => (
              <div key={post.id} className="flex items-center gap-4 p-5">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-foreground">{post.title}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    /blog/{post.slug} ·{" "}
                    {post.status === "published" &&
                    post.published_at &&
                    new Date(post.published_at) > new Date() ? (
                      <span className="font-semibold text-primary">
                        Agendado para{" "}
                        {new Date(post.published_at).toLocaleString("pt-BR", {
                          dateStyle: "short",
                          timeStyle: "short",
                          timeZone: "America/Sao_Paulo",
                        })}
                      </span>
                    ) : post.status === "published" ? (
                      <span className="font-semibold text-accent-ink">Publicado</span>
                    ) : (
                      <span className="font-semibold text-primary">Rascunho</span>
                    )}
                  </p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => startEdit(post)} aria-label="Editar">
                  <Pencil className="size-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => remove(post)} aria-label="Excluir">
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
